import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  GetLocationTypeResponseType,
  GetLocationListByTypeResponseType,
} from 'features/Location/@types/location'
import {
  ReplenishmentConfigItemType,
  GetReplenishmentConfigParameterType,
} from 'features/SupplyOrder/services/replenishment'

export type ReplenishmentConfigInitialStateType = {
  isLoading: boolean
  locationTypeList: GetLocationTypeResponseType['data']
  locationDataList: GetLocationListByTypeResponseType[]
  selectedLocationType: Nullable<GetLocationTypeResponseType['data'][number]>
  selectedLocationName: Nullable<GetLocationListByTypeResponseType>
  params: GetReplenishmentConfigParameterType
  replenishmentConfigListData: ReplenishmentConfigItemType[]
  modalUpdate: {
    isOpen: boolean
    isSubmittingData: boolean
  }
  currentIndexSelected: number
  mapStorageIdToName: Record<number, string>
}

const initialState: ReplenishmentConfigInitialStateType = {
  isLoading: true,
  locationTypeList: [],
  locationDataList: [],
  selectedLocationType: null,
  selectedLocationName: null,
  params: {
    location_ids: [],
    product_name_or_sku: '',
    page_size: 10,
    page_index: 1,
  },
  replenishmentConfigListData: [],
  modalUpdate: {
    isOpen: false,
    isSubmittingData: false,
  },
  currentIndexSelected: -1,
  mapStorageIdToName: {},
}

const SLICE_NAME = 'REPLENISHMENT_CONFIG'

const replenishmentConfigSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setLocationTypeList: (state, action: PayloadAction<GetLocationTypeResponseType['data']>) => {
      state.locationTypeList = action.payload
    },
    setLocationDataList: (state, action: PayloadAction<GetLocationListByTypeResponseType[]>) => {
      state.locationDataList = action.payload
    },
    setSelectedLocationType: (
      state,
      action: PayloadAction<ReplenishmentConfigInitialStateType['selectedLocationType']>,
    ) => {
      state.selectedLocationType = action.payload
    },
    setSelectedLocation: (
      state,
      action: PayloadAction<ReplenishmentConfigInitialStateType['selectedLocationName']>,
    ) => {
      state.selectedLocationName = action.payload
    },
    setParams: (state, action: PayloadAction<Partial<GetReplenishmentConfigParameterType>>) => {
      state.params = {
        ...state.params,
        ...action.payload,
      }
    },
    setReplenishmentConfigListData: (
      state,
      action: PayloadAction<ReplenishmentConfigItemType[]>,
    ) => {
      state.replenishmentConfigListData = action.payload
    },
    toggleModalUpdate: (state, action: PayloadAction<boolean>) => {
      state.modalUpdate.isOpen = action.payload
    },
    setModalUpdate: (
      state,
      action: PayloadAction<Partial<ReplenishmentConfigInitialStateType['modalUpdate']>>,
    ) => {
      state.modalUpdate = {
        ...state.modalUpdate,
        ...action.payload,
      }
    },
    resetModalUpdate: (state) => {
      state.modalUpdate = initialState.modalUpdate
      state.currentIndexSelected = -1
    },
    setCurrentIndexSelected: (state, action: PayloadAction<number>) => {
      state.currentIndexSelected = action.payload
    },
    setMapStorageIdToName: (
      state,
      action: PayloadAction<ReplenishmentConfigInitialStateType['mapStorageIdToName']>,
    ) => {
      state.mapStorageIdToName = action.payload
    },
  },
})

export const {
  setIsLoading,
  setLocationTypeList,
  setLocationDataList,
  setSelectedLocationType,
  setSelectedLocation,
  setParams,
  setReplenishmentConfigListData,
  toggleModalUpdate,
  setModalUpdate,
  resetModalUpdate,
  setCurrentIndexSelected,
  setMapStorageIdToName,
} = replenishmentConfigSlice.actions

export default replenishmentConfigSlice.reducer
